import { createContext, Suspense, useCallback, useContext, useId, useLayoutEffect, useMemo, useReducer } from "react";
import { useLocation } from "react-router-dom";
import RouteFallback from "./RouteFallback";

const ReadinessContext = createContext(null);

// A fresh route object starts unready during render, not in a later effect.
// Old route cleanups can therefore never mark a new page ready.
export const PublicPageReadinessProvider = ({ authLoading, children }) => {
  const { key, pathname, search } = useLocation();
  const route = useMemo(() => ({ id: `${key}:${pathname}${search}`, committed: false, pending: new Set() }), [key, pathname, search]);
  const [, refresh] = useReducer((value) => value + 1, 0);
  const setPending = useCallback((id, pending) => {
    const wasPending = route.pending.has(id);
    if (pending) route.pending.add(id);
    else route.pending.delete(id);
    if (wasPending !== pending) refresh();
  }, [route]);
  const setCommitted = useCallback((committed) => {
    if (route.committed !== committed) {
      route.committed = committed;
      refresh();
    }
  }, [route]);
  const value = {
    ready: !authLoading && route.committed && route.pending.size === 0,
    setPending, setCommitted,
  };
  return <ReadinessContext.Provider value={value}>{children}</ReadinessContext.Provider>;
};

export const usePublicPageReady = () => useContext(ReadinessContext)?.ready ?? false;

// Presentation only: observe existing initial-loading flags; do not fetch,
// change booking/auth state, or wait for unrelated background requests.
export function usePublicPageLoading(loading) {
  const id = useId();
  const setPending = useContext(ReadinessContext)?.setPending;
  useLayoutEffect(() => {
    setPending?.(id, Boolean(loading));
    return () => setPending?.(id, false);
  }, [id, loading, setPending]);
}

// Placed INSIDE Suspense: lazy routes count as ready only after they commit.
export const PublicRouteContent = ({ children }) => {
  const setCommitted = useContext(ReadinessContext)?.setCommitted;
  useLayoutEffect(() => {
    setCommitted?.(true);
    return () => setCommitted?.(false);
  }, [setCommitted]);
  return children;
};

export const PublicRouteBoundary = ({ children }) => {
  const { pathname } = useLocation();
  // Router transitions otherwise retain an old already-ready route while a
  // new chunk suspends. Reset only this boundary, never the shared Footer.
  // Query changes retain the page instance (and existing booking form state).
  return <Suspense key={pathname} fallback={<RouteFallback />}>
    <PublicRouteContent>{children}</PublicRouteContent>
  </Suspense>;
};